import React from 'react';
import { t } from '../i18n';
import { fmtDay } from '../lib/format';
import { expiryPhrase, displayLabel, neverExpires, isOptional } from '../lib/ledger';

// The "can this tech be paid?" banner. Shown at the top of the technician's
// ledger and documents tabs. `forVendor` adds the explanation the tech needs;
// without it the banner is the terse admin-side version.
export function ComplianceBanner({ compliance, forVendor }) {
  if (!compliance) return null;

  // The server also sends ready-made `blockers` / `expiringSoon` strings, but
  // those are English. For the technician we rebuild the list from `items` so
  // the document names and day counts land in their language.
  const summarise = (statuses, englishFallback, blockingOnly) => {
    if (!forVendor) return englishFallback.join(' · ');
    return (compliance.items || [])
      .filter(i => statuses.includes(i.status))
      // Only what actually holds the money. Listing all four under "payments
      // are on hold" told a tech to go and find two documents that were never
      // going to change anything, and left the two that would buried in the
      // middle of the sentence.
      .filter(i => !(blockingOnly && isOptional(i.type)))
      // "Liability Insurance — 12 days ago" leaves the reader to work out what
      // happened 12 days ago. The word that matters goes in front of it.
      .map(i => `${displayLabel(i)} — ${
        i.status === 'missing' ? t('lg.notOnFile')
          : neverExpires(i.type) ? t('lg.onFile', 'On file')
          : i.status === 'expired' ? `${t('lg.expired', 'Expired')} ${expiryPhrase(i.daysLeft)}`
          : expiryPhrase(i.daysLeft)}`)
      .join(' · ');
  };

  if (compliance.status === 'blocked') {
    const blocking = summarise(['expired', 'missing'], compliance.blockers, true);
    return (
      <div className="alert alert-red">
        <div>
          <strong>{forVendor ? t('lg.blockedTitle') : 'Payments blocked — paperwork not up to date.'}</strong>
          {/* Empty only if the server said blocked while nothing blocks, which
              it cannot do on its own — but an empty red line reads as a
              problem with no name, so it is simply not drawn. */}
          {blocking && <div style={{ marginTop: 4 }}>{blocking}</div>}
          {forVendor && <div style={{ marginTop: 6 }}>{t('lg.blockedBody')}</div>}
        </div>
      </div>
    );
  }

  if (compliance.status === 'expiring') {
    return (
      <div className="alert alert-amber">
        <div>
          <strong>{forVendor ? t('lg.expiringTitle') : 'Renewal coming up for this vendor'}</strong>
          <div style={{ marginTop: 4 }}>{summarise(['expiring'], compliance.expiringSoon)}</div>
          {forVendor && <div style={{ marginTop: 6 }}>{t('lg.expiringBody')}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="alert alert-green">
      <div>
        <strong>{forVendor ? t('lg.currentTitle') : 'Paperwork is current — clear to be paid.'}</strong>
        {forVendor && <> {t('lg.currentBody')}</>}
      </div>
    </div>
  );
}

// The per-document breakdown: insurance, business license, occupational
// license, plus any extra documents the tech added.
export default function CompliancePanel({ compliance, onUpload, onDelete }) {
  if (!compliance) return null;
  const all = [...(compliance.items || []), ...(compliance.extras || [])];

  // Colour follows what the document DOES to the tech, not just its status. An
  // optional licence that was never provided costs them nothing, so it draws
  // neutral — a red card beside a red banner for a document a handyman will
  // never hold reads as a problem they cannot fix.
  const colorFor = (item) => {
    if (item.status === 'missing' && isOptional(item.type)) return 'var(--muted)';
    return ({
      valid: 'var(--green)', expiring: 'var(--amber)', expired: 'var(--red)', missing: 'var(--red)'
    }[item.status] || 'var(--slate)');
  };

  return (
    <div>
      {all.map(item => (
        <div key={item._id || item.type} className="lead-card" style={{ cursor: 'default', borderLeft: `3px solid ${colorFor(item)}` }}>
          <div style={{ fontSize: 14 }}>{displayLabel(item)}</div>
          <div style={{ fontSize: 12, color: colorFor(item), marginTop: 2 }}>
            {item.status === 'missing' && isOptional(item.type)
              ? t('lg.optionalOnly', 'Optional — only if your trade requires one')
              : item.status === 'missing'
              ? t('lg.notOnFile')
              : neverExpires(item.type)
                ? <>{t('lg.onFile', 'On file')} · {t('lg.noExpiry', 'No expiry — signed once')}</>
                : <>{t('lg.expiresOn')} {fmtDay(item.expiresAt)} · {expiryPhrase(item.daysLeft)}</>}
          </div>
          {(item.issuer || item.number) && (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
              {[item.issuer, item.number && `#${item.number}`].filter(Boolean).join(' · ')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {item.documentUrl && (
              <button className="btn btn-outline btn-sm" onClick={() => window.open(item.documentUrl, '_blank')}>{t('lg.viewDocument')}</button>
            )}
            {onUpload && (
              <button className={`btn btn-sm ${item.status === 'valid' || (item.status === 'missing' && isOptional(item.type)) ? 'btn-outline' : 'btn-primary'}`} onClick={() => onUpload(item)}>
                {item.status === 'missing' ? t('lg.upload') : neverExpires(item.type) ? t('lg.replace', 'Replace') : t('lg.uploadRenewal')}
              </button>
            )}
            {onDelete && item._id && (
              <button className="btn btn-ghost btn-sm" onClick={() => onDelete(item)}>×</button>
            )}
          </div>
        </div>
      ))}

      {onUpload && (
        <button className="btn btn-outline btn-sm" style={{ marginTop: 6 }} onClick={() => onUpload({ type: 'other', status: 'missing' })}>
          {t('lg.addAnother')}
        </button>
      )}
    </div>
  );
}
